import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import Modal from 'modules/core/components/Modal'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { usePayrollBundlePageContext } from 'modules/payroll_bundle/components/PayrollBundle'
import ResultLine from 'modules/payroll_bundle/components/common/components/ResultLine'
import { useBulkImportSupplements } from 'modules/payroll_bundle/lib/bulkImportSupplementsContext'
import { SELECT_DATA, UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'
import DropSpreadsheetZone from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/_common/DropSpreadsheetZone'
import { DataImports } from 'modules/payroll_bundle/store/collections/dataImports'

const ImportModal = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { selectedPolicy, policyPeriod } = usePayrollBundlePageContext()

  const [uploadedFile, setUploadedFile] = React.useState<(File | null | undefined) | null>(null)

  const dataImportsCollection: DataImports = React.useRef(new DataImports([])).current
  const { supplementDispatch } = useBulkImportSupplements()

  const onDropFile = async (files: File[]) => {
    const file = files[0]

    if (!file) return

    setUploadedFile(file)
  }

  const downloadFile = async () => {
    if (!uploadedFile) return

    const { success, value } = await asyncAction(
      () => dataImportsCollection.importFile(uploadedFile),
      {
        successMessage: i18n.t('payroll_bundle.payroll_results.uploaded_successfully'),
      }
    )

    if (success) {
      supplementDispatch({
        type: UPDATE_DATA,
        payload: {
          worksheetData: value,
          showUploadModal: false,
          filename: uploadedFile.name,
          bulkImportSupplementStage: SELECT_DATA,
        },
      })

      navigate(
        navigation.payrollBundle.policy.period.cycles.supplements.import.fullPath({
          policyId: selectedPolicy?.id,
          periodId: policyPeriod?.id,
        })
      )
    }
  }

  const onClose = () => {
    supplementDispatch({
      type: UPDATE_DATA,
      payload: {
        showUploadModal: false,
      },
    })
  }

  return (
    <Modal
      title={i18n.t('payroll_bundle.import_supplements.import.title')}
      description={i18n.t('payroll_bundle.import_supplements.import.description')}
      illustration="file-upload"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={downloadFile} type="primary" disabled={!uploadedFile}>
            {i18n.t('payroll_bundle.import_supplements.import.process_file')}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        <Box gap="s16" flexGrow>
          <DropSpreadsheetZone
            onDrop={onDropFile}
            multiple={false}
            placeholder={i18n.t('payroll_bundle.import_supplements.import.dropzone_placeholder')}
          />
          {uploadedFile && (
            <ResultLine filename={uploadedFile.name} onRemove={() => setUploadedFile(null)} />
          )}
        </Box>
        <Box>
          <HelpCenterLink name="import_supplements">
            {i18n.t('payroll_bundle.import_supplements.import.helper_link')}
          </HelpCenterLink>
        </Box>
      </Box>
    </Modal>
  )
}

export default observer(ImportModal)
